import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, RotateCcw, Palette, Type, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import {
  DEFAULT_THEME, FONT_LIBRARY, COLOR_PRESETS, loadGoogleFont,
} from "./themeConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_FIELDS = [
  ["primary", "Primary", "Buttons, links, key accents"],
  ["accent", "Accent", "Highlights, icons, CTAs"],
  ["secondary", "Secondary", "Soft fills, badges"],
  ["background", "Background", "Page background tint"],
  ["text_color", "Text", "Headings & body text colour"],
];

const FontSelect = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-xs font-medium text-foreground/60 mb-2 block">{label}</label>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => { loadGoogleFont(e.target.value); onChange(e.target.value); }}
      style={{ fontFamily: `"${value}"` }}
      className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-primary transition-colors"
    >
      {FONT_LIBRARY.map((g) => (
        <optgroup key={g.category} label={g.category}>
          {g.fonts.map((f) => (
            <option key={f} value={f} style={{ fontFamily: `"${f}"` }}>{f}</option>
          ))}
        </optgroup>
      ))}
    </select>
  </div>
);

export const AppearanceTab = ({ auth }) => {
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/theme`)
      .then((r) => {
        setTheme({ ...DEFAULT_THEME, ...r.data });
        loadGoogleFont(r.data.heading_font);
        loadGoogleFont(r.data.body_font);
        loadGoogleFont(r.data.display_font);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setTheme((t) => ({ ...t, [k]: v }));

  const applyPreset = (p) => {
    setTheme((t) => ({ ...t, primary: p.primary, accent: p.accent, secondary: p.secondary, background: p.background }));
    toast.success(`Applied "${p.name}" palette`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        primary: theme.primary,
        accent: theme.accent,
        secondary: theme.secondary,
        background: theme.background,
        text_color: theme.text_color,
        heading_font: theme.heading_font,
        body_font: theme.body_font,
        display_font: theme.display_font,
        base_font_size: Number(theme.base_font_size),
        line_height: Number(theme.line_height),
      };
      const r = await axios.put(`${API}/admin/theme`, payload, auth());
      setTheme({ ...DEFAULT_THEME, ...r.data });
      toast.success("Theme saved — it's now live on your website.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save theme.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      const r = await axios.post(`${API}/admin/theme/reset`, {}, auth());
      setTheme({ ...DEFAULT_THEME, ...r.data });
      toast.success("Theme reset to default.");
    } catch (e) {
      toast.error("Couldn't reset theme.");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <div className="py-20 text-center text-foreground/50"><Loader2 className="mx-auto animate-spin" size={26} /></div>;
  }

  const inputCls = "w-28 rounded-xl border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary transition-colors uppercase";

  return (
    <div data-testid="appearance-tab">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary"><Palette size={20} /></span>
        <div>
          <h2 className="font-heading text-2xl font-medium">Appearance</h2>
          <p className="text-sm text-foreground/55">Customize the colours, fonts and text of your public website.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="reset-theme-btn"
            onClick={reset}
            disabled={resetting}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary transition-colors disabled:opacity-60"
          >
            {resetting ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Reset to default
          </button>
          <button
            data-testid="save-theme-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & publish
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: controls */}
        <div className="space-y-6">
          {/* Palettes */}
          <div className="bg-card border border-border rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-4 text-sm font-medium text-foreground/70"><Palette size={16} /> Colour scheme library</div>
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
              {COLOR_PRESETS.map((p) => (
                <button
                  key={p.name}
                  data-testid={`preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => applyPreset(p)}
                  className="group rounded-2xl border border-border p-2.5 hover:border-primary transition-colors text-left"
                >
                  <div className="flex gap-1 mb-2">
                    {[p.primary, p.accent, p.secondary].map((c, i) => (
                      <span key={i} className="h-6 flex-1 rounded-md" style={{ background: c }} />
                    ))}
                  </div>
                  <span className="text-[11px] font-medium text-foreground/60 group-hover:text-primary">{p.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom colours */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground/70"><SlidersHorizontal size={16} /> Custom colours</div>
            {COLOR_FIELDS.map(([key, label, hint]) => (
              <div key={key} className="flex items-center gap-4">
                <input
                  type="color"
                  data-testid={`color-${key}`}
                  value={theme[key]}
                  onChange={(e) => set(key, e.target.value)}
                  className="h-11 w-14 rounded-xl border border-border bg-white cursor-pointer p-1"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs text-foreground/45">{hint}</div>
                </div>
                <input
                  data-testid={`color-hex-${key}`}
                  value={theme[key]}
                  onChange={(e) => set(key, e.target.value)}
                  className={inputCls}
                />
              </div>
            ))}
          </div>

          {/* Fonts */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground/70"><Type size={16} /> Font library</div>
            <FontSelect label="Heading font" value={theme.heading_font} onChange={(v) => set("heading_font", v)} testId="font-heading-select" />
            <FontSelect label="Display font (large headings)" value={theme.display_font} onChange={(v) => set("display_font", v)} testId="font-display-select" />
            <FontSelect label="Body font" value={theme.body_font} onChange={(v) => set("body_font", v)} testId="font-body-select" />
          </div>

          {/* Typography */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-5">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground/70"><SlidersHorizontal size={16} /> Text styling</div>
            <div>
              <div className="flex justify-between text-xs mb-2"><span className="text-foreground/60">Body text size</span><span className="font-medium text-primary">{theme.base_font_size}px</span></div>
              <input
                type="range" min="14" max="20" step="1"
                data-testid="base-font-size"
                value={theme.base_font_size}
                onChange={(e) => set("base_font_size", Number(e.target.value))}
                className="w-full accent-[var(--tw-accent,#0e9083)]"
                style={{ accentColor: theme.primary }}
              />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-2"><span className="text-foreground/60">Paragraph line spacing</span><span className="font-medium text-primary">{Number(theme.line_height).toFixed(2)}</span></div>
              <input
                type="range" min="1.3" max="2.1" step="0.05"
                data-testid="line-height"
                value={theme.line_height}
                onChange={(e) => set("line_height", Number(e.target.value))}
                className="w-full"
                style={{ accentColor: theme.primary }}
              />
            </div>
          </div>
        </div>

        {/* Right: live preview */}
        <div className="lg:sticky lg:top-6 h-fit">
          <div className="text-xs uppercase tracking-widest text-foreground/40 mb-3">Live preview</div>
          <div
            data-testid="theme-preview"
            className="rounded-3xl border border-border overflow-hidden shadow-[0_30px_70px_-45px_rgba(0,0,0,0.4)]"
            style={{ background: theme.background, fontFamily: `"${theme.body_font}"`, fontSize: `${theme.base_font_size}px`, lineHeight: theme.line_height }}
          >
            <div className="p-8">
              <span
                className="inline-block text-[11px] font-bold uppercase tracking-[0.24em]"
                style={{ color: theme.accent }}
              >
                Peoplelab Advisory
              </span>
              <h3
                className="mt-3 font-medium tracking-tight"
                style={{ fontFamily: `"${theme.display_font}"`, color: theme.text_color, fontSize: `${theme.base_font_size * 2.1}px`, lineHeight: 1.05 }}
              >
                Build the team that builds your future.
              </h3>
              <p className="mt-4" style={{ color: theme.text_color, opacity: 0.75 }}>
                People, process and possibilities — the HR partner behind teams that go the
                distance. This preview reflects your chosen colours, fonts and spacing.
              </p>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button
                  className="rounded-full px-5 py-2.5 text-sm font-medium text-white"
                  style={{ background: theme.primary }}
                >
                  Get in touch
                </button>
                <span
                  className="rounded-full px-4 py-2 text-xs font-medium"
                  style={{ background: `${theme.secondary}33`, color: theme.primary }}
                >
                  Recruitment
                </span>
                <span
                  className="rounded-full px-4 py-2 text-xs font-medium text-white"
                  style={{ background: theme.accent }}
                >
                  Careers
                </span>
              </div>
            </div>
            <div className="px-8 py-5 flex items-center justify-between" style={{ background: theme.primary }}>
              <span className="text-white/90 text-sm" style={{ fontFamily: `"${theme.heading_font}"` }}>Peoplelab</span>
              <span className="text-white/70 text-xs italic">People | Process | Possibilities</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
